import { Tooltip } from '@mui/material'
import type { DigitalTwinSetting } from 'api/digitalTwin/digitalTwin.types'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'ui/atoms'
import Icon from 'ui/atoms/Icon/Icon'

type SettingEventLinkProps = {
  eventName: string | undefined
  onClick: () => void
}

/** Opens the operational event a setting was created for. */
export function SettingEventLink({ eventName, onClick }: SettingEventLinkProps): ReactNode {
  const { t } = useTranslation()

  return (
    <Icon
      icon="fal fa-calendar-week"
      large
      data-testid="event-link-icon" // e2e: event-linked marker icon — do not remove
      tooltip={{ title: t('View event') + ': ' + (eventName ?? '-'), arrow: true, placement: 'right' }}
      onClick={onClick}
    />
  )
}

type SettingManageButtonProps = {
  setting: DigitalTwinSetting
  canEdit: boolean | undefined
  onEditSetting: (setting: DigitalTwinSetting) => void
}

/** Opens a setting for editing, or explains why the user may not. */
export function SettingManageButton({ setting, canEdit, onEditSetting }: SettingManageButtonProps): ReactNode {
  const { t } = useTranslation()
  const noPermission = t('Unfortunately, you do not have permission to change settings on this user/facility')

  return (
    <Tooltip title={canEdit ? '' : noPermission} arrow placement="top">
      <span>
        <Button
          link
          aria-label={t('Edit setting')}
          onClick={() => onEditSetting(setting)}
          disabled={!canEdit}
          data-testid="manage-setting-button" // e2e: setting edit button — do not remove
          disabledTooltip={noPermission}
          icon={'fal fa-edit'}
        >
          {''}
        </Button>
      </span>
    </Tooltip>
  )
}
